import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '../config/firebase';

export type BillingCatalog = {
  planId: string;
  monthlyPriceId: string;
  annualPriceId: string;
  monthlyAmountInCents: number;
  annualAmountInCents: number;
  currency: string;
};

export type BillingPaymentDiscount = {
  yearlyPercent?: number;
  yearlySavingsCents?: number;
  upgradeCreditMs?: number;
  upgradeCreditDays?: number;
  openMonths?: number;
  deltaMonthlyCents?: number;
};

export type BillingPaymentChange = {
  previousSeatCount?: number | null;
  seatCount?: number | null;
  previousVolumePacks?: number | null;
  volumePacks?: number | null;
  deltaSeats?: number | null;
  deltaVolumePacks?: number | null;
  openMonths?: number | null;
  renewalDueDate?: string | null;
  nextRenewalChargeCents?: number | null;
};

export type BillingPaymentSummary = {
  id: string;
  status: string | null;
  paymentMethod: string | null;
  grossAmountInCents: number | null;
  confirmedAt: string | null;
  createdAt: string | null;
  billingPeriodStart: string | null;
  billingPeriodEnd: string | null;
  kind?: string | null;
  description?: string | null;
  billingInterval?: string | null;
  seatCount?: number | null;
  volumePacks?: number | null;
  monthlyCents?: number | null;
  chargeCents?: number | null;
  discount?: BillingPaymentDiscount | null;
  change?: BillingPaymentChange | null;
  asaasInvoiceUrl?: string | null;
  asaasTransactionReceiptUrl?: string | null;
  nfsePdfUrl?: string | null;
  providerPaymentId?: string | null;
  refundStatus?: string | null;
  refundedAt?: string | null;
  refundReason?: string | null;
  /** Server-computed CDC art. 49 eligibility (first buy / upgrades only). */
  cdcRefundEligible?: boolean;
  cdcRefundDaysLeft?: number | null;
};

export type BillingProfile = {
  legalName: string | null;
  email: string | null;
  cpfCnpj: string | null;
  phone: string | null;
  postalCode: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressStreet?: string | null;
  addressNeighborhood?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  hasProviderCustomer: boolean;
};

export type BillingSubscriptionSummary = {
  accountId: string;
  hasPaidAccess: boolean;
  featureKey: string;
  entitlement: {
    status?: string;
    validFrom?: string;
    validUntil?: string;
    source?: string;
  } | null;
  billingProfile?: BillingProfile | null;
  subscriptions: Array<Record<string, unknown>>;
  payments?: BillingPaymentSummary[];
};

// ── Seat management (settled seat model 2026-08-10) ────────────────────────

export type SeatOccupant = {
  phoneE164: string;
  phoneDisplay: string;
  uid: string | null;
  displayName: string | null;
  assignedAt: number;
};

export type SeatSlot = {
  occupant: SeatOccupant | null;
  vacatedCycle: string | null;
  reassignedCycle: string | null;
};

// ── Pack quantities + cancel (Asaas quantity work + Stage C, 2026-08-10) ───

export type PackChangeResult = {
  ok: boolean;
  unchanged?: boolean;
  /** Yearly increase needs Pay-now checkout before capacity/charge apply. */
  requiresCheckout?: boolean;
  seatCount: number;
  volumePacks: number;
  pendingSeatCount: number | null;
  pendingVolumePacks: number | null;
  /** @deprecated Prefer seatCount. */
  seatPacks?: number;
  pendingSeatPacks?: number | null;
  nextChargeCents: number;
  monthlyCents: number;
  nextDueDate: string | null;
  billingInterval?: 'MONTHLY' | 'YEARLY';
  /** Yearly mid-cycle stub charged now (0 for monthly / decreases). */
  midCycleChargeCents?: number;
  midCycleOpenMonths?: number;
  /** X9 saved the decrease; Asaas could not change the CC value (tokenização). */
  asaasSyncPending?: boolean;
};

export async function updateSubscriptionPacks(
  seatCount: number,
  volumePacks: number,
): Promise<PackChangeResult> {
  const fn = httpsCallable(getFirebaseFunctions(), 'updateSubscriptionPacks', { timeout: 30_000 });
  const res = await fn({ seatCount, volumePacks });
  return res.data as PackChangeResult;
}

export async function cancelMySubscription(): Promise<{
  ok: boolean;
  alreadyCancelling?: boolean;
  accessUntil: string | null;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'cancelMySubscription', { timeout: 30_000 });
  const res = await fn({});
  return res.data as { ok: boolean; alreadyCancelling?: boolean; accessUntil: string | null };
}

export async function assignSeat(phone: string, name?: string): Promise<{ ok: boolean; registered: boolean }> {
  const fn = httpsCallable(getFirebaseFunctions(), 'assignSeat');
  const res = await fn({ phone, name: name || undefined });
  return res.data as { ok: boolean; registered: boolean };
}

export async function unassignSeat(phoneE164: string): Promise<{ ok: boolean }> {
  const fn = httpsCallable(getFirebaseFunctions(), 'unassignSeat');
  const res = await fn({ phoneE164 });
  return res.data as { ok: boolean };
}

export async function fetchBillingPlans(): Promise<{
  catalog: BillingCatalog;
  plans: unknown[];
  prices: unknown[];
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'getBillingPlans');
  const res = await fn({});
  return res.data as {
    catalog: BillingCatalog;
    plans: unknown[];
    prices: unknown[];
  };
}

export async function fetchBillingSubscription(): Promise<BillingSubscriptionSummary> {
  const fn = httpsCallable(getFirebaseFunctions(), 'getBillingSubscription');
  const res = await fn({});
  return res.data as BillingSubscriptionSummary;
}

/** Refresh Asaas invoice/receipt URLs for one payment (lazy GET). */
export async function getPaymentDocumentUrls(paymentId: string): Promise<BillingPaymentSummary> {
  const fn = httpsCallable(getFirebaseFunctions(), 'getPaymentDocumentUrls', { timeout: 30_000 });
  const res = await fn({ paymentId });
  return res.data as BillingPaymentSummary;
}

/** CDC art. 49 — refund first buy / upgrade payment within 7 days. */
export async function requestPaymentRefund(paymentId: string): Promise<{
  ok: boolean;
  alreadyRefunded?: boolean;
  kind?: string;
  payment?: BillingPaymentSummary;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'requestPaymentRefund', { timeout: 60_000 });
  const res = await fn({ paymentId });
  return res.data as {
    ok: boolean;
    alreadyRefunded?: boolean;
    kind?: string;
    payment?: BillingPaymentSummary;
  };
}

export type CreditCardPayload = {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
};

export type CreditCardHolderInfoPayload = {
  name: string;
  email: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  phone?: string;
  mobilePhone?: string;
  addressComplement?: string | null;
  addressStreet?: string | null;
  addressNeighborhood?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
};

/** Confirm yearly mid-cycle pack upgrade (explicit Pay now from checkout). */
export async function confirmPackUpgradeCheckout(input: {
  seatCount: number;
  volumePacks: number;
  useSavedCard?: boolean;
  creditCard?: CreditCardPayload;
  creditCardHolderInfo?: CreditCardHolderInfoPayload;
}): Promise<PackChangeResult> {
  const fn = httpsCallable(getFirebaseFunctions(), 'updateSubscriptionPacks', { timeout: 60_000 });
  const res = await fn({
    seatCount: input.seatCount,
    volumePacks: input.volumePacks,
    confirmMidCycle: true,
    useSavedCard: input.useSavedCard === true,
    ...(input.creditCard ? { creditCard: input.creditCard } : {}),
    ...(input.creditCardHolderInfo
      ? { creditCardHolderInfo: input.creditCardHolderInfo }
      : {}),
  });
  return res.data as PackChangeResult;
}

export async function startCardCheckout(payload: {
  legalName: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  billingInterval: 'MONTHLY' | 'YEARLY';
  seatCount?: number;
  /** @deprecated Prefer seatCount. */
  seatPacks?: number;
  volumePacks?: number;
  creditCard: CreditCardPayload;
  creditCardHolderInfo: CreditCardHolderInfoPayload;
}): Promise<{
  subscriptionId: string;
  providerSubscriptionId: string;
  status: string;
  message: string;
  packUpdated?: boolean;
  entitlementActivated?: boolean;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'startMonthlyCheckout', {
    timeout: 60_000,
  });
  const res = await fn(payload);
  return res.data as {
    subscriptionId: string;
    providerSubscriptionId: string;
    status: string;
    message: string;
    packUpdated?: boolean;
    entitlementActivated?: boolean;
  };
}

/** @deprecated use startCardCheckout */
export const startMonthlyCheckout = startCardCheckout;

export async function requestEmailVerification(email: string): Promise<{
  ok: boolean;
  emailSent: boolean;
  message?: string;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'requestRecoveryEmailVerification');
  const res = await fn({ email });
  return res.data as { ok: boolean; emailSent: boolean; message?: string };
}

export async function confirmEmailCode(code: string): Promise<{
  ok: boolean;
  recoveryEmailVerified: boolean;
  recoveryEmail?: string;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'confirmRecoveryEmailCode');
  const res = await fn({ code });
  return res.data as {
    ok: boolean;
    recoveryEmailVerified: boolean;
    recoveryEmail?: string;
  };
}

export async function confirmEmailToken(token: string): Promise<{
  ok: boolean;
  recoveryEmailVerified: boolean;
  recoveryEmail?: string;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'confirmRecoveryEmailVerification');
  const res = await fn({ token });
  return res.data as {
    ok: boolean;
    recoveryEmailVerified: boolean;
    recoveryEmail?: string;
  };
}

export async function deleteUserAccount(): Promise<{
  success?: boolean;
  partial?: boolean;
}> {
  const fn = httpsCallable(getFirebaseFunctions(), 'deleteUserAccount');
  let res;
  try {
    res = await fn({});
  } catch (firstError) {
    // Match mobile: retry once on transient internal after cleanup.
    const code =
      firstError && typeof firstError === 'object' && 'code' in firstError
        ? String((firstError as { code: string }).code)
        : '';
    if (code.includes('internal')) {
      await new Promise((r) => setTimeout(r, 1500));
      res = await fn({});
    } else {
      throw firstError;
    }
  }
  return (res.data || {}) as { success?: boolean; partial?: boolean };
}

/** R1 — find/create self contact after phone auth (non-blocking wrapper below). */
export function ensureContactForPhoneAuth(): Promise<{
  ok: true;
  contactId: string;
}> {
  const fn = httpsCallable<Record<string, never>, { ok: true; contactId: string }>(
    getFirebaseFunctions(),
    'ensureContactForPhoneAuth',
  );
  return fn({}).then((res) => res.data);
}

export async function ensureContactAfterPhoneVerify(): Promise<string | null> {
  try {
    const result = await ensureContactForPhoneAuth();
    return result.contactId;
  } catch (err) {
    console.warn('ensureContactForPhoneAuth failed (non-blocking):', err);
    return null;
  }
}

function callableRawMessage(err: unknown): string {
  return err && typeof err === 'object' && 'message' in err
    ? String((err as { message: string }).message)
    : '';
}

/** In-place pack update cannot run — send the user through card checkout instead. */
export function shouldFallbackToCheckout(err: unknown): boolean {
  const raw = callableRawMessage(err);
  return (
    raw.includes('no_active_subscription')
    || raw.includes('card_required')
    || /cliente inv[aá]lido|n[aã]o informado/i.test(raw)
    || /Provider update failed/i.test(raw)
  );
}

export function callableErrorMessage(err: unknown): string {
  const raw = callableRawMessage(err);
  const code = raw.includes('card_required')
    ? 'card_required'
    : raw.includes('midcycle_charge_failed')
      ? 'midcycle_charge_failed'
      : raw.includes('no_active_subscription')
        ? 'no_active_subscription'
        : raw;
  if (code === 'card_required') {
    return 'Precisamos do cartão cadastrado para cobrar a extensão até a renovação. Atualize o pagamento na Conta ou troque para o fluxo de checkout.';
  }
  if (code === 'midcycle_charge_failed') {
    return 'Não foi possível cobrar a extensão no cartão. Tente de novo ou fale com o suporte.';
  }
  if (code === 'no_active_subscription') {
    return 'Não há uma assinatura ativa para alterar. Continue para o pagamento e contrate o plano Equipe.';
  }
  if (/active or pending subscription already exists|Paid access is already active/i.test(raw)) {
    return 'Esta conta já tem Equipe ativo. Ampliar assentos atualiza a mesma assinatura — a diferença entra na próxima fatura, sem um plano novo.';
  }
  if (/cliente inv[aá]lido|n[aã]o informado/i.test(raw)) {
    return 'Não encontramos seu cadastro de pagamento. Tente de novo — se o erro continuar, fale com o suporte.';
  }
  if (raw) return raw;
  return 'Something went wrong. Please try again.';
}
